import "./marker.css";

import { debounce } from "@mui/material";
import L from "leaflet";
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";

import { EventHandler } from "verdi-web-client/HelperFunctions";
import { shouldBlockMapEntityCardOpen } from "@/features/add-device/should-block-map-entity-card-open";
import { openZustandCard } from "@/features/cards/shared/legacy/open-zustand-card";
import { getConnectedZones } from "@/services/device-attributes/attributes/general";
import { getCardTypeFromEntity } from "@/features/cards/shared/card-routing/card-types";
import { useStore } from "../../../store/store";
import FocusContext from "../focus-context";
import type MapEntityBase from "../map-entities/map-entity-base";
import { ClusterMarkerMemo } from "./components/cluster-marker";
import { DeviceMarkerMemo } from "./components/device-marker";
import { ZoneHighlightPolygon } from "./components/zone-highlight-polygon";
import { getExpandedBounds } from "./constants";
import { computeIconState, makeClusterPropsFromContext, makeMarkerPropsFromContextState } from "./core/marker-state";
import { usePanToAvoidCard } from "./hooks/use-pan-to-avoid-card";
import type { ClusterMapItem, DeviceMapItem } from "./types";

export const recomputeMarkerStateEvent = new EventHandler({}, 1000);
export const deviceMarkerUpdateEvent = new EventHandler({}, 1000);

interface MarkerLayerProps {
    iconMapEntities: MapEntityBase[];
    mapComponent: L.Map;
}
/**
 * Responsible for rendering the icons at the proper location on the map, manages icon state
 * that is shared across all icons such as zoom, hover, focus, etc.
 */
export const MarkerLayer = memo(({ iconMapEntities, mapComponent }: MarkerLayerProps) => {
    const selectedMapEntity = useStore((state) => state.selectedMapEntity);

    const [currentMapZoom, setCurrentMapZoom] = useState(mapComponent ? mapComponent.getZoom() : 16);
    const [clusterItems, setClusterItems] = useState<ClusterMapItem[]>([]);
    const [deviceItems, setDeviceItems] = useState<DeviceMapItem[]>([]);
    const [visibleMapBounds, setVisibleMapBounds] = useState<L.LatLngBounds | undefined>(undefined);
    const [hoveredIconId, setHoveredIconId] = useState<string | null>(null);

    usePanToAvoidCard();

    // Cache for icon states
    const iconStatePropsCache = useRef(new Map<string, ReturnType<typeof makeMarkerPropsFromContextState>>());

    // Hack to force re-render of icons when the focus context changes
    const [, setTriggeredIconUpdates] = useState<number>(0);
    useEffect(() => {
        const debouncedSetTriggeredIconUpdates = debounce(() => {
            setTriggeredIconUpdates((prev) => prev + 1);
        }, 150);

        deviceMarkerUpdateEvent.addListener(() => {
            debouncedSetTriggeredIconUpdates();
        });
        return () => {
            deviceMarkerUpdateEvent.removeListener(debouncedSetTriggeredIconUpdates);
        };
    }, []);

    /**
     * Hack to force a recomputation of memoized map state
     * This is necessary since due to legacy reasons, our map entities objects/arrays are not stateful so they do not trigger re-renders when their contents change and so cannot
     * be added as a dependency to the map state memoized value. Therefore, we need a mechanism to trigger a recomputation of the map state in an event-driven manner
     *
     * We introduce this "dummy" counter state as a dependency of the map state memoized value so updating the state
     * will force a recomputation of the map state.
     */
    const [triggerMapStateComputation, setTriggerMapStateComputation] = useState<number>(0);

    // Event listener for when the cluster/device marker state needs to be recomputed (eg. Device is repositioned)
    useEffect(() => {
        const forceMapStateComputation = async () => {
            if (!mapComponent) {
                return;
            }
            setTriggerMapStateComputation((prev) => prev + 1);
        };

        recomputeMarkerStateEvent.addListener(forceMapStateComputation);
        return () => {
            recomputeMarkerStateEvent.removeListener(forceMapStateComputation);
        };
    }, [currentMapZoom, iconMapEntities, mapComponent]);

    const mapState = useMemo(async () => {
        if (mapComponent) {
            return computeIconState({
                iconMapEntities,
                mapComponent,
            });
        }
        return undefined;

        // We include mapStateEventCounter as a hack to force recomputation of map state via an event call.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [iconMapEntities, mapComponent, triggerMapStateComputation]);

    // Populate state when zoom level changes
    useEffect(() => {
        const populateState = async () => {
            const stateForAllZoomLevels = await mapState;

            if (stateForAllZoomLevels?.[currentMapZoom] !== undefined) {
                const stateForCurrentZoomLevel = stateForAllZoomLevels[currentMapZoom];
                setClusterItems(stateForCurrentZoomLevel.clusterMapItems);
                setDeviceItems(stateForCurrentZoomLevel.deviceMapItems);
            }
        };
        populateState();
    }, [mapState, currentMapZoom]);

    useEffect(() => {
        const onMapUpdate = () => {
            setCurrentMapZoom(mapComponent.getZoom());
            setVisibleMapBounds(getExpandedBounds(mapComponent.getBounds()));
        };

        if (mapComponent) {
            mapComponent.on("zoomend", onMapUpdate);
            mapComponent.on("moveend", onMapUpdate);
            onMapUpdate();
        }
        return () => {
            if (mapComponent) {
                mapComponent.off("zoomend", onMapUpdate);
                mapComponent.off("moveend", onMapUpdate);
            }
        };
    }, [mapComponent]);

    const handleDeviceClick = useCallback((_e: React.MouseEvent, mapEntity: MapEntityBase) => {
        // Prevent a usurp (opening a new card and dismissing the current one) if a persistent card type is already open
        const currentCardType = FocusContext.currentInfoCard?.currentInfoCardType;
        if (shouldBlockMapEntityCardOpen(currentCardType)) {
            return;
        }
        setHoveredIconId(null); // on mobile iOS, onMouseLeave is not called after a tap (unlike mobile mouse hover), so clear explicitly to dismiss the highlight polygon
        openZustandCard({ entity: mapEntity, cardType: getCardTypeFromEntity(mapEntity) });
    }, []);

    const visibleDeviceItems = useMemo(() => {
        if (!visibleMapBounds) return [];
        return deviceItems.filter((item) => visibleMapBounds.contains(new L.LatLng(item.lat, item.long)));
    }, [deviceItems, visibleMapBounds]);

    const visibleClusterItems = useMemo(() => {
        if (!visibleMapBounds) return [];
        return clusterItems.filter((item) => visibleMapBounds.contains(new L.LatLng(item.lat, item.long)));
    }, [clusterItems, visibleMapBounds]);

    const handleIconHover = useCallback((id: string | null) => {
        setHoveredIconId(id);
    }, []);

    // If the hovered icon leaves the visible area (e.g. map pans it out of bounds), clear hover so
    // the polygon doesn't reappear when the icon scrolls back into view with no active mouse hover.
    useEffect(() => {
        if (!hoveredIconId) return;
        const isStillVisibleAsDevice = visibleDeviceItems.some(
            (item) => (item.mapEntity.id ?? item.mapEntity.uid) === hoveredIconId,
        );
        const isStillVisibleAsCluster = visibleClusterItems.some((item) => item.id === hoveredIconId);
        if (!isStillVisibleAsDevice && !isStillVisibleAsCluster) setHoveredIconId(null);
    }, [visibleDeviceItems, visibleClusterItems, hoveredIconId]);

    // Callback to get the dimming state of  to prevent unnecessary re-computations
    const getIsDimmed = useCallback(
        (mapEntityId: string) => {
            const deviceItem = deviceItems.find((item) => (item.mapEntity.id ?? item.mapEntity.uid) === mapEntityId);
            if (!deviceItem) return false;

            if (selectedMapEntity?.model.category === "aoi") {
                // Don't dim anything when an AOI is selected
                return false;
            }

            // A device is dimmed if:
            // 1. A device is selected and this isn't it
            // 2. A zone is selected and this device isn't in it
            if (selectedMapEntity?.model.category === "zone") {
                const connectedZones = getConnectedZones({ model: deviceItem.mapEntity.model });
                return !connectedZones.some((zone) => zone.id === selectedMapEntity.id);
            }
            return selectedMapEntity !== null && mapEntityId !== selectedMapEntity.id;
        },
        [selectedMapEntity, deviceItems],
    );

    // Get icon props from cache or compute
    const getIconStateProps = useCallback((mapEntity: MapEntityBase) => {
        const state = mapEntity.focusState;
        const cacheKey = `${mapEntity.id ?? mapEntity.uid}-${state}`;

        let props = iconStatePropsCache.current.get(cacheKey);
        if (!props) {
            props = makeMarkerPropsFromContextState(state);
            iconStatePropsCache.current.set(cacheKey, props);
        }

        return props;
    }, []);

    // Memoize the device items filtering and mapping
    const visibleDeviceMarkers = useMemo(() => {
        if (!visibleMapBounds) return [];
        return visibleDeviceItems
            .filter((item) => item.clusterKey === undefined)
            .map((deviceMapItem) => {
                const { highlighted, hidden } = getIconStateProps(deviceMapItem.mapEntity);
                const deviceType = deviceMapItem.mapEntity.model.deviceType;
                const iconId = deviceMapItem.mapEntity.id ?? deviceMapItem.mapEntity.uid;
                return (
                    <DeviceMarkerMemo
                        key={`${iconId}_${deviceType}`}
                        deviceMapItem={deviceMapItem}
                        currentMapZoom={currentMapZoom}
                        isHovered={hoveredIconId === iconId}
                        mapComponent={mapComponent}
                        onDeviceClick={handleDeviceClick}
                        onIconHover={handleIconHover}
                        highlighted={highlighted}
                        hidden={hidden}
                        isDimmed={getIsDimmed(iconId)}
                    />
                );
            });
    }, [
        visibleDeviceItems,
        visibleMapBounds,
        currentMapZoom,
        hoveredIconId,
        mapComponent,
        handleDeviceClick,
        handleIconHover,
        getIsDimmed,
        getIconStateProps,
    ]);

    // Memoize cluster props computation
    const clusterPropsMap = useMemo(() => {
        const propsMap = new Map<string, { dimmed: boolean; hidden: boolean }>();

        clusterItems.forEach((clusterMapItem) => {
            propsMap.set(
                clusterMapItem.id,
                makeClusterPropsFromContext(clusterMapItem, deviceItems, selectedMapEntity),
            );
        });

        return propsMap;
    }, [clusterItems, deviceItems, selectedMapEntity]);

    // Optimize cluster marker rendering
    const visibleClusterMarkers = useMemo(() => {
        if (!visibleMapBounds) return [];
        return visibleClusterItems.map((clusterMapItem) => {
            const { dimmed, hidden } = clusterPropsMap.get(clusterMapItem.id) ?? { dimmed: false, hidden: false };
            return (
                <ClusterMarkerMemo
                    key={clusterMapItem.id}
                    clusterMapItem={clusterMapItem}
                    deviceItems={deviceItems}
                    isHovered={hoveredIconId === clusterMapItem.id}
                    mapComponent={mapComponent}
                    onIconHover={handleIconHover}
                    dimmed={dimmed}
                    hidden={hidden}
                />
            );
        });
    }, [
        visibleClusterItems,
        visibleMapBounds,
        clusterPropsMap,
        deviceItems,
        hoveredIconId,
        mapComponent,
        handleIconHover,
    ]);

    if (!mapComponent) {
        return null;
    }
    return (
        <>
            {visibleDeviceMarkers}
            {visibleClusterMarkers}
            {hoveredIconId && (
                <ZoneHighlightPolygon
                    mapEntity={
                        visibleDeviceItems.find((item) => (item.mapEntity.id ?? item.mapEntity.uid) === hoveredIconId)
                            ?.mapEntity
                    }
                />
            )}
        </>
    );
});
